import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { LayoutGrid, Heart, PlusCircle, MessageSquare, User, Bell, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Browse", icon: LayoutGrid, id: "browse" },
  { to: "/saved", label: "Saved", icon: Heart, id: "saved" },
  { to: "/sell", label: "Sell", icon: PlusCircle, id: "sell" },
  { to: "/inbox", label: "Inbox", icon: MessageSquare, id: "inbox" },
  { to: "/profile", label: "Profile", icon: User, id: "profile" },
];

const Dot = ({ n, testId }) =>
  n > 0 ? (
    <span data-testid={testId} className="absolute -top-1 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
      {n > 9 ? "9+" : n}
    </span>
  ) : null;

const TopBar = () => {
  const { user, badges } = useAuth();
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black">F</span>
          <span className="font-extrabold tracking-tight text-slate-900 text-lg">FollowThrough</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1 ml-4">
          {NAV.map((n) => (
            <NavLink key={n.id} to={n.to} end={n.to === "/"} data-testid={`topnav-${n.id}`}
              className={({ isActive }) => `relative px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${isActive ? "text-blue-700 bg-blue-50" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
              <span className="relative">{n.label}{n.id === "inbox" && <Dot n={badges.inbox} testId="topnav-inbox-badge" />}</span>
            </NavLink>
          ))}
          {user && <NavLink to="/dashboard" data-testid="topnav-dashboard" className={({ isActive }) => `px-3 py-2 rounded-xl text-sm font-semibold ${isActive ? "text-blue-700 bg-blue-50" : "text-slate-600 hover:bg-slate-100"}`}>Selling</NavLink>}
          {user && <NavLink to="/pickups" data-testid="topnav-pickups" className={({ isActive }) => `px-3 py-2 rounded-xl text-sm font-semibold ${isActive ? "text-blue-700 bg-blue-50" : "text-slate-600 hover:bg-slate-100"}`}>Pickups</NavLink>}
          {user?.role === "admin" && <NavLink to="/admin" data-testid="topnav-admin" className="px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Admin</NavLink>}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {user ? (
            <Link to="/notifications" data-testid="notifications-bell" aria-label="Notifications" className="relative h-10 w-10 rounded-xl hover:bg-slate-100 flex items-center justify-center">
              <Bell className="h-5 w-5 text-slate-700" />
              <span className="absolute top-1 right-1"><Dot n={badges.notifications} testId="notifications-badge" /></span>
            </Link>
          ) : (
            user === false && <Link to="/login" data-testid="topbar-signin" className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">Sign in</Link>
          )}
        </div>
      </div>
    </header>
  );
};

const BottomNav = () => {
  const { badges } = useAuth();
  return (
    <nav data-testid="bottom-nav" className="fixed bottom-0 inset-x-0 z-50 md:hidden bg-white/95 backdrop-blur-md border-t border-slate-200 pb-[env(safe-area-inset-bottom)]">
      <div className="grid grid-cols-5">
        {NAV.map((n) => (
          <NavLink key={n.id} to={n.to} end={n.to === "/"} data-testid={`bottomnav-${n.id}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[11px] font-semibold ${isActive ? "text-blue-600" : "text-slate-500"}`}>
            <span className="relative">
              <n.icon className={n.id === "sell" ? "h-7 w-7" : "h-5 w-5"} strokeWidth={n.id === "sell" ? 2.2 : 2} />
              {n.id === "inbox" && <Dot n={badges.inbox} testId="bottomnav-inbox-badge" />}
            </span>
            {n.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export const Layout = ({ children }) => {
  const { pathname } = useLocation();
  const inThread = pathname.startsWith("/inbox/");
  const [demo, setDemo] = useState(false);
  useEffect(() => {
    api.get("/meta").then(({ data }) => setDemo(data.demo_mode)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <TopBar />
      {demo && <div data-testid="demo-banner" className="bg-slate-900 text-slate-200 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center gap-2">
          <Info className="h-3.5 w-3.5 text-blue-400 shrink-0" />
          <span>Demo mode: listings marked <b className="text-white">Demo</b> are fictional and can't be bought. Free to use — purchases happen in person.</span>
        </div>
      </div>}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${inThread ? "pt-0 pb-0 md:pt-6 md:pb-6" : "pt-6 pb-28 md:pb-12"}`}>{children}</main>
      {!inThread && <BottomNav />}
    </div>
  );
};
